/**
 * @ken/module-fleet-management — frontend entry point (Fleet, slice 1:
 * trucks and trailers; PRD 42 Task 2).
 *
 * Routes, static before parameterised:
 *   /fleet            the list (search `?q=` and filter `?status=` in the URL;
 *                     a truck opens in a pop-up over it, ADR-0229)
 *   /fleet/new        Add a truck, full page
 *   /fleet/paste      Paste a list
 *   /fleet/$unitId    one truck on its own page
 *   /fleet/drivers, /fleet/drivers/new, /fleet/drivers/$driverId (slice 2,
 *                     PRD 46): literal paths, so they win over /fleet/$unitId
 *
 * The YAML manifest is the only copy of the module's declarations (CV-19).
 */

import { lazyScreen, type AnyRoute, type CreateModuleRoutes } from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound } from "@tanstack/react-router";

import { validateDriversSearch, validateNewDriverSearch } from "./drivers-search";
import { validateFleetListSearch } from "./search";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadDriversPages = () => import("./routes/drivers-pages");

// Screens download the first time they are opened (ADR-040, BND-01).
const FleetListPage = lazyScreen(() => import("./routes/fleet-list"), "FleetListPage");
const FleetNewPage = lazyScreen(() => import("./routes/fleet-new"), "FleetNewPage");
const FleetPastePage = lazyScreen(() => import("./routes/fleet-paste"), "FleetPastePage");
const FleetUnitPage = lazyScreen(() => import("./routes/fleet-unit-page"), "FleetUnitPage");
const FleetDriversPage = lazyScreen(() => import("./routes/drivers-list"), "FleetDriversPage");
const FleetDriverNewPage = lazyScreen(loadDriversPages, "FleetDriverNewPage");
const FleetDriverPage = lazyScreen(loadDriversPages, "FleetDriverPage");

/** ADR-0251: the Driver tab on the employee record, a lazy chunk. */
export const recordTabs = {
  EmployeeDriverTab: lazyScreen(() => import("./components/drivers/EmployeeDriverTab"), "EmployeeDriverTab"),
};

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet",
    validateSearch: validateFleetListSearch,
    component: FleetListPage,
  });
  const newRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/new",
    component: FleetNewPage,
  });
  const pasteRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/paste",
    component: FleetPastePage,
  });
  const unitRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/$unitId",
    // A segment that could never be a record id stops here, before any read.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { unitId?: string }).unitId)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    component: FleetUnitPage,
  });
  const driversRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/drivers",
    validateSearch: validateDriversSearch,
    component: FleetDriversPage,
  });
  const driverNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/drivers/new",
    validateSearch: validateNewDriverSearch,
    component: FleetDriverNewPage,
  });
  const driverRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fleet/drivers/$driverId",
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { driverId?: string }).driverId)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    component: FleetDriverPage,
  });
  return [listRoute, newRoute, pasteRoute, driversRoute, driverNewRoute, driverRoute, unitRoute];
};

export type { FleetListSearch } from "./search";
